/**
 * Bir etiketi mm ölçülerinde HTML olarak çizer.
 *
 * Neden HTML (Konva raster değil): tarayıcı/PDF çıktısında metin vektör kalır,
 * yazıcı kendi çözünürlüğünde keskin basar. Raster gönderirsek 600 dpi lazer
 * yazıcıya 203 dpi görüntü basmış oluruz. Ayrıca yüzlerce etiketlik önizleme
 * raster ile ağırlaşıyor.
 *
 * CSS'in mm birimi yazdırmada fiziksel ölçüye karşılık gelir; bu yüzden tüm
 * ölçüler doğrudan mm olarak yazılır, px dönüşümü yapılmaz.
 */

import React, { useLayoutEffect, useMemo, useRef } from 'react';
import {
  LabelElement,
  LabelTemplate,
  PlaceholderData,
  getDesignSize,
  isElementVisible,
  resolvePlaceholders,
} from '../../../types/labelTemplate.types';
import { getCachedFittedBarcodeImage, getCachedQr } from '../utils/barcodeRenderer';
import { resolveImageSrc } from '../assets/domesticBadge';

export interface LabelHtmlRendererProps {
  template: LabelTemplate;
  data?: PlaceholderData;
  /** Ekranda önizleme ölçeği. Yazdırmada 1 kalmalı. */
  scale?: number;
  /** Etiket sınırını kesikli çizgiyle göster (yalnızca ekran) */
  showBounds?: boolean;
  /**
   * Şablonun döndürmesini uygulamaz; etiket tasarım ölçüsünde çizilir.
   * Ham yazıcı çıktısında (raster) döndürme piksel seviyesinde yapılıyor —
   * html2canvas'ın CSS transform desteği güvenilir değil.
   */
  skipRotation?: boolean;
}

/**
 * Şablonun döndürme açısına göre tasarım katmanını fiziksel etiket alanına
 * oturtan CSS dönüşümü. Döndürme sonrası içerik sola/yukarı taşıdığı için
 * telafi ötelemesi gerekir.
 */
function rotationTransform(rotation: number, designWidthMm: number, designHeightMm: number): string {
  switch (rotation) {
    case 90:
      return `translateX(${designHeightMm}mm) rotate(90deg)`;
    case 180:
      return `translate(${designWidthMm}mm, ${designHeightMm}mm) rotate(180deg)`;
    case 270:
      return `translateY(${designWidthMm}mm) rotate(270deg)`;
    default:
      return 'none';
  }
}

/**
 * Metni kutuya sığdırmak için punto küçültür.
 *
 * Yakınsama doğrudan DOM üzerinde yapılır, state ile değil: eskiden her ölçüm
 * turu setState ile yeni bir React render tetikliyordu ve 100 etiketlik bir
 * baskıda (etiket × metin alanı × tur) binlerce render birikiyordu — ürün
 * eklemek ve PDF üretmek dakikalara çıkıyordu. Tek layout etkisi içinde
 * ölçüp yazmak aynı sonucu React'i hiç meşgul etmeden veriyor.
 */
const ShrinkText: React.FC<{
  el: LabelElement;
  text: string;
  enabled: boolean;
}> = ({ el, text, enabled }) => {
  const ref = useRef<HTMLDivElement>(null);
  const baseSize = el.fontSizeMm ?? 3;

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    node.style.fontSize = `${baseSize}mm`;
    if (!enabled) return;

    let size = baseSize;
    // Oransal küçültme birkaç turda oturur; tur sayısı sonsuz döngüye karşı sınırlı
    for (let i = 0; i < 12; i++) {
      if (node.scrollHeight <= node.clientHeight + 1 || size <= 0.6) break;
      const factor = node.clientHeight / node.scrollHeight;
      const next = Math.max(0.6, size * factor * 0.96);
      if (Math.abs(next - size) < 0.02) break;
      size = next;
      node.style.fontSize = `${size}mm`;
    }
  }, [baseSize, text, enabled, el.widthMm, el.heightMm]);

  return (
    <div
      ref={ref}
      style={{
        width: '100%',
        height: '100%',
        fontSize: `${baseSize}mm`,
        fontFamily: el.fontFamily ?? 'Arial',
        fontWeight: el.bold ? 700 : 400,
        fontStyle: el.italic ? 'italic' : 'normal',
        color: el.color ?? '#000000',
        textAlign: el.align ?? 'left',
        lineHeight: el.lineHeight ?? 1.2,
        overflow: 'hidden',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {text}
    </div>
  );
};

const HtmlElement: React.FC<{ el: LabelElement; data?: PlaceholderData; dpi: number }> = React.memo(({ el, data, dpi }) => {
  const base: React.CSSProperties = {
    position: 'absolute',
    left: `${el.xMm}mm`,
    top: `${el.yMm}mm`,
    width: `${el.widthMm}mm`,
    height: `${el.heightMm}mm`,
    opacity: el.opacity ?? 1,
    transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
    transformOrigin: 'left top',
  };

  switch (el.type) {
    case 'text': {
      const text = resolvePlaceholders(el.text ?? '', data);
      return (
        <div style={base}>
          <ShrinkText el={el} text={text} enabled={!!el.autoShrink} />
        </div>
      );
    }

    case 'barcode': {
      const value = resolvePlaceholders(el.barcodeValue ?? '', data);
      // Yazıcının dot ölçüsünde üretilir ki önizleme ile termal çıktı aynı
      // barkodu göstersin (bkz. generateFittedBarcodeCanvas)
      const toDots = (mm: number) => Math.round((mm * dpi) / 25.4);
      const image = getCachedFittedBarcodeImage(value, {
        format: el.barcodeFormat ?? 'CODE128',
        boxWidthPx: toDots(el.widthMm),
        boxHeightPx: toDots(el.heightMm),
        showText: el.showBarcodeText ?? true,
        color: el.color ?? '#000000',
      });

      if (!image) {
        return (
          <div
            style={{
              ...base,
              border: '0.2mm dashed #d32f2f',
              color: '#d32f2f',
              fontSize: '2mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            {value ? 'Barkod üretilemedi' : 'Barkod boş'}
          </div>
        );
      }

      // Kutuyu doldurmak yerine üretilen oran korunur; yatayda esnetmek
      // çubuk kalınlıklarını bozardı. Artan boşluk barkodun sessiz bölgesi.
      const widthRatio = image.widthPx / Math.max(1, toDots(el.widthMm));

      return (
        <div style={{ ...base, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={image.url}
            alt=""
            style={{
              width: `${Math.min(100, widthRatio * 100)}%`,
              height: '100%',
              display: 'block',
              imageRendering: 'pixelated',
            }}
          />
        </div>
      );
    }

    case 'qr': {
      const value = resolvePlaceholders(el.qrValue ?? '', data);
      const url = getCachedQr(value, { size: 512, errorLevel: el.qrErrorLevel ?? 'M', color: el.color ?? '#000000' });
      const side = Math.min(el.widthMm, el.heightMm);

      if (!url) {
        return (
          <div
            style={{
              ...base,
              width: `${side}mm`,
              height: `${side}mm`,
              border: '0.2mm dashed #d32f2f',
              color: '#d32f2f',
              fontSize: '2mm',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            QR boş
          </div>
        );
      }

      return (
        <div style={{ ...base, width: `${side}mm`, height: `${side}mm` }}>
          <img
            src={url}
            alt=""
            style={{ width: '100%', height: '100%', display: 'block', imageRendering: 'pixelated' }}
          />
        </div>
      );
    }

    case 'image':
      if (!el.src) return <div style={{ ...base, border: '0.2mm dashed #bdbdbd' }} />;
      return (
        <div style={base}>
          {/* crossOrigin şart: kütüphane resimleri ayrı origin'den (img.*) geliyor,
              onsuz html2canvas rasterlemesinde canvas taint olup baskı boş çıkar */}
          <img
            src={resolveImageSrc(el.src)}
            alt=""
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          />
        </div>
      );

    case 'rect':
      return (
        <div
          style={{
            ...base,
            backgroundColor: el.fillColor === 'transparent' ? 'transparent' : el.fillColor ?? 'transparent',
            border: `${el.strokeWidthMm ?? 0.3}mm solid ${el.strokeColor ?? '#000000'}`,
            borderRadius: el.cornerRadiusMm ? `${el.cornerRadiusMm}mm` : undefined,
            boxSizing: 'border-box',
          }}
        />
      );

    case 'line':
      return (
        <div
          style={{
            ...base,
            height: `${Math.max(0.1, el.heightMm)}mm`,
            backgroundColor: el.fillColor ?? '#000000',
          }}
        />
      );

    default:
      return null;
  }
});

const LabelHtmlRenderer: React.FC<LabelHtmlRendererProps> = React.memo(({
  template,
  data,
  scale = 1,
  showBounds,
  skipRotation,
}) => {
  const design = getDesignSize(template);
  const visibleElements = useMemo(
    () => template.elements.filter((el) => isElementVisible(el, data)),
    [template.elements, data],
  );

  return (
    <div
      style={{
        // Dış kap fiziksel etiket ölçüsünde; içerik döndürülerek buna oturur.
        // skipRotation'da döndürme yapılmadığı için kap tasarım ölçüsünde olur.
        width: skipRotation ? `${design.widthMm}mm` : `${template.labelWidthMm}mm`,
        height: skipRotation ? `${design.heightMm}mm` : `${template.labelLengthMm}mm`,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: template.backgroundColor ?? '#ffffff',
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: 'left top',
        outline: showBounds ? '0.2mm dashed #90a4ae' : undefined,
        // Arka plan ve renkler baskıda korunsun
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${design.widthMm}mm`,
          height: `${design.heightMm}mm`,
          transform: skipRotation
            ? undefined
            : rotationTransform(template.rotation, design.widthMm, design.heightMm),
          transformOrigin: 'left top',
        }}
      >
        {visibleElements.map((el) => (
          <HtmlElement key={el.id} el={el} data={data} dpi={template.dpi} />
        ))}
      </div>
    </div>
  );
});

export default LabelHtmlRenderer;
